import React, { useEffect, useState } from 'react';
import { X, UserCheck } from 'lucide-react';
import { orderReviewAPI } from '../../services/api';
import Select from '../Select';
import useDialog from '../../hooks/useDialog';
import { roleLabel } from '../attendance/attendanceStatus';

// Who should check an order before it goes to the supplier. Anybody active can be chosen except
// the person sharing it: the order comes back to them once the reviewer is done.

const ShareForReviewDialog = ({ title, subtitle, confirmText = 'Share for review', onClose, onShare }) => {
  const ref = useDialog(onClose);
  const [people, setPeople] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [reviewerId, setReviewerId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    orderReviewAPI.reviewers()
      .then((list) => { if (live) setPeople(list || []); })
      .catch((e) => { if (live) setLoadError(e.message || 'Could not load the people to choose from'); });
    return () => { live = false; };
  }, []);

  const options = (people || []).map((p) => ({ value: p.id, label: `${p.fullName} · ${roleLabel(p.role)}` }));
  const chosen = (people || []).find((p) => p.id === reviewerId);

  const submit = async (e) => {
    e.preventDefault();
    if (!reviewerId || busy) return;
    setBusy(true);
    try {
      await onShare(reviewerId, note.trim(), chosen);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="share-review-title"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b flex items-start justify-between gap-3">
          <div>
            <h2 id="share-review-title" className="text-lg font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {loadError ? (
            <p className="text-sm text-red-700">{loadError}</p>
          ) : people === null ? (
            <p className="text-sm text-gray-500">Loading the people to choose from…</p>
          ) : (
            <Select label="Who should review it?" required value={reviewerId} onChange={setReviewerId}
              placeholder="Choose a person" options={options} buttonClassName="p-2.5 rounded-lg" />
          )}
          <div>
            <label htmlFor="share-review-note" className="block text-sm font-medium text-gray-700 mb-1">Anything to look at in particular?</label>
            <textarea id="share-review-note" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Check the syrups against the store room"
              className="w-full border border-gray-200 rounded-lg p-2.5 text-sm" />
          </div>
          <p className="text-xs text-gray-500">
            {chosen ? `${chosen.fullName} sees it under Inventory, Order Reviews.` : 'They see it under Inventory, Order Reviews.'} They
            can change packs, add or take off items, with a reason for each. It comes back to you to accept, and nothing goes to the supplier until you do.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium">Cancel</button>
            <button type="submit" disabled={!reviewerId || busy}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
              <UserCheck className="w-4 h-4" /> {busy ? 'Sharing…' : confirmText}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ShareForReviewDialog;
